import React, { useMemo, useState } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background-color: var(--bg-secondary);
  min-height: 60px;
  max-height: 140px;
  overflow-y: auto;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, var(--primary-color) 0%, #764ba2 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;

  button {
    background: rgba(239, 68, 68, 0.18);
    border: none;
    color: #fecaca;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.28);
    }

    svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
    }
  }
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background-color: var(--card-bg);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SmallButton = styled.button`
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border-color);
  background: var(--card-highlight-bg);
  color: var(--text-primary);
  white-space: nowrap;

  &:hover {
    background: var(--border-color);
  }
`;

const List = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-bg);
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--card-highlight-bg);

  .meta {
    font-size: 0.85rem;
    color: var(--text-secondary);
    font-weight: 600;
  }
`;

const ListBody = styled.div`
  max-height: 240px;
  overflow-y: auto;
`;

const StateRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0.8rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--bg-secondary);
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(99, 91, 255, 0.3);
    background: var(--bg-secondary);
  }
`;

const StateLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .name {
    font-weight: 700;
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  .abbr {
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-weight: 600;
  }
`;

const Check = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 2px solid ${(p) => (p.checked ? "var(--primary-color)" : "var(--border-color)")};
  background: ${(p) => (p.checked ? "var(--primary-color)" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    color: white;
    display: ${(p) => (p.checked ? "block" : "none")};
  }
`;

function normalizeState(state) {
  return {
    id: state?.id,
    name: state?.state_name || state?.name || "",
    abbr: state?.state_abbreviation || state?.state_code || state?.code || ""
  };
}

export default function StatesMultiSelect({
  states,
  selectedIds,
  onChange,
  placeholder = "Type to filter states (e.g., Texas, TX)..."
}) {
  const [filter, setFilter] = useState("");

  const normalizedStates = useMemo(
    () => (Array.isArray(states) ? states : []).map(normalizeState).filter((s) => s.id != null),
    [states]
  );

  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return normalizedStates;
    return normalizedStates.filter((s) => {
      const hay = `${s.name} ${s.abbr}`.toLowerCase();
      return hay.includes(q);
    });
  }, [filter, normalizedStates]);

  const selectedStates = useMemo(() => {
    const map = new Map(normalizedStates.map((s) => [s.id, s]));
    return (selectedIds || []).map((id) => map.get(id)).filter(Boolean);
  }, [normalizedStates, selectedIds]);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const clearAll = () => onChange([]);

  const selectAllVisible = () => {
    const next = new Set(selectedSet);
    filtered.forEach((s) => next.add(s.id));
    onChange(Array.from(next));
  };

  const removeChip = (id) => toggle(id);

  const onFilterKeyDown = (e) => {
    if (e.key === "Enter" && filtered.length > 0) {
      e.preventDefault();
      toggle(filtered[0].id);
    }
  };

  return (
    <Wrapper>
      <SelectedChips>
        {selectedStates.length === 0 ? (
          <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
            No states selected. Use search + click to select quickly.
          </span>
        ) : (
          selectedStates.map((s) => (
            <Chip key={s.id}>
              {s.abbr || s.name}
              <button type="button" onClick={() => removeChip(s.id)} aria-label={`Remove ${s.name}`}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </Chip>
          ))
        )}
      </SelectedChips>

      <ControlsRow>
        <FilterInput
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={onFilterKeyDown}
          placeholder={placeholder}
        />
        <SmallButton type="button" onClick={selectAllVisible}>
          Select visible
        </SmallButton>
        <SmallButton type="button" onClick={clearAll}>
          Clear
        </SmallButton>
      </ControlsRow>

      <List>
        <ListHeader>
          <div className="meta">
            Showing {filtered.length} state{filtered.length !== 1 ? "s" : ""} • Selected{" "}
            {selectedSet.size}
          </div>
        </ListHeader>
        <ListBody>
          {filtered.map((s) => {
            const checked = selectedSet.has(s.id);
            return (
              <StateRow type="button" key={s.id} onClick={() => toggle(s.id)}>
                <StateLeft>
                  <div className="name">{s.name}</div>
                  <div className="abbr">{s.abbr}</div>
                </StateLeft>
                <Check checked={checked}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Check>
              </StateRow>
            );
          })}
        </ListBody>
      </List>
    </Wrapper>
  );
}


